import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { updateReminder, selectRemindersLoading, selectAllReminders, fetchReminderById } from 'store/slices/remindersSlice';
import Page from 'components/layout/Page/Page';
import ReminderTabs from './components/ReminderTabs/ReminderTabs';
import ReminderForm from './components/ReminderForm/ReminderForm';

export default function EditReminder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  const [fetchStatus, setFetchStatus] = useState('idle');

  const loading = useSelector(selectRemindersLoading);
  const reminders = useSelector(selectAllReminders);

  const reminder = useMemo(() =>
    reminders.find(r => r._id === id || r.id === id),
    [reminders, id]
  );

  useEffect(() => {
    if (!reminder && id && fetchStatus === 'idle') {
      dispatch(fetchReminderById(id))
        .unwrap()
        .then(() => setFetchStatus('done'))
        .catch(() => setFetchStatus('done'));
    }
  }, [reminder, id, fetchStatus, dispatch]);

  const onSubmit = async (data) => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      // Remove _id/id from data if present to avoid sending it in body if API doesn't like it
      // eslint-disable-next-line no-unused-vars
      const { _id, id: reminderId, ...updateData } = data;
      await dispatch(updateReminder({ id, data: updateData })).unwrap();
      navigate('/reminders');
    } catch (_error) {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const isFetching = !reminder && (loading || fetchStatus === 'idle');
  const isNotFound = !reminder && !loading && fetchStatus === 'done';

  return (
    <Page title="Edit Reminder" headerActions={<ReminderTabs />}>
      <div className="max-w-2xl mx-auto">
        {isNotFound ? (
            <div className="text-center p-8 text-gray-500">
                Reminder not found.
            </div>
        ) : (
            <ReminderForm
            key={reminder?._id || 'loading'}
            defaultValues={reminder || {}}
            onSubmit={onSubmit}
            loading={isFetching}
            saving={isSaving}
            requireChanges
            submitLabel="Update"
            />
        )}
      </div>
    </Page>
  );
}
